/**
 * Sandboxes v2 list-page data: applications, live sandboxes, and the
 * claim / destroy mutations. Shapes are the API's
 * (`apps/api/src/sandboxes-v2/routes.ts`, `uploads.ts`).
 *
 * @module features/sandboxes/hooks/useSandboxesV2
 */
import { useInfiniteQuery, type InfiniteData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SANDBOXES_V2_KEY as SANDBOXES_KEY } from '../../../hooks/useClaimSandboxV2';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import type { SandboxV2EnvironmentReadiness } from '../../../hooks/useSandboxV2EnvironmentReadiness';

export type SandboxV2Application = {
  id: string;
  name: string;
  delivery: 'github' | 'upload';
  repo: string | null;
  latestVersion: { version: string; tags: string[]; at: string } | null;
  environments: Array<{
    id: string;
    name: string;
    /** The sandbox environment name a claim targets. */
    environment: string;
    readiness: SandboxV2EnvironmentReadiness;
  }>;
};

export type SandboxV2SandboxStatus = 'claiming' | 'ready' | 'failed' | 'destroyed';

export type SandboxV2ListItem = {
  id: string;
  applicationId: string;
  environment: string;
  version: string;
  /** The tags the version's push carries; empty for a commit or an untagged push. */
  versionTags: string[];
  status: SandboxV2SandboxStatus;
  error: string | null;
  portUrls: Record<string, string>;
  leaseExpiresAt: string | null;
  createdAt: string;
  owner:
    | { kind: 'user'; id: string; label: string }
    | {
        kind: 'run';
        id: string;
        label: string;
        workflowId: string;
        status: 'queued' | 'running' | 'success' | 'failed' | 'inconclusive' | 'canceled' | 'timeout';
      }
    | { kind: 'adhoc_test'; id: string; label: string }
    | null;
};

type Page<T> = { data: T[]; nextCursor: string | null };

const LIVE_STATUSES = 'claiming,ready,failed';

export async function getJson<T>(path: string, fallback: string): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!res.ok) throw await toApiError(res, fallback);
  return (await res.json()) as T;
}

export function useSandboxV2Applications(options: { enabled?: boolean } = {}) {
  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['sandbox-v2-applications'],
    queryFn: async () =>
      (await getJson<{ data: SandboxV2Application[] }>('/org/sandboxes-v2/applications', 'Failed to load applications')).data,
    // Warming finishes in the background; poll only until nothing is warming.
    refetchInterval: (query) =>
      query.state.data?.some((app) => app.environments.some((env) => env.readiness.status === 'warming')) ? 10_000 : false,
  });
}

/** Live (claiming/ready/failed) sandboxes, polled while any is still claiming. */
export function useSandboxV2Sandboxes(options: { mine: boolean; applicationId: string | null }) {
  const { mine, applicationId } = options;
  return useInfiniteQuery({
    queryKey: [...SANDBOXES_KEY, { mine, applicationId }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ status: LIVE_STATUSES, limit: '25' });
      if (mine) params.set('mine', 'true');
      if (applicationId) params.set('applicationId', applicationId);
      if (pageParam) params.set('cursor', pageParam);
      return getJson<Page<SandboxV2ListItem>>(`/org/sandboxes-v2/sandboxes?${params}`, 'Failed to load sandboxes');
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.data.some((row) => row.status === 'claiming')) ? 5000 : false,
  });
}

export function useDestroySandboxV2() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sandboxId: string) => {
      const res = await fetch(`${API_URL}/org/sandboxes-v2/sandboxes/${sandboxId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to destroy the sandbox');
    },
    // DELETE only queues the destroy job, so a refetch now would return the row as `ready` again and undo
    // this removal. The server marks it destroyed when its job runs; the next poll or focus refetch picks that up.
    onSuccess: (_data, sandboxId) => {
      queryClient.setQueriesData<InfiniteData<Page<SandboxV2ListItem>>>({ queryKey: SANDBOXES_KEY }, (data) =>
        data && {
          ...data,
          pages: data.pages.map((page) => ({ ...page, data: page.data.filter((row) => row.id !== sandboxId) })),
        }
      );
    },
  });
}
